import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { TooltipHost } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';
import last from 'lodash/last';
import BundleVersionListRow from '../BundleVersionListRow';
import DetailsList from '../DetailsList';
import styles from './BundleVersionList.style';
import { bundleStatus, formatDateString } from '../../utils';
import appStyles from '../../App.style';
import { BundleVersionProvider } from '../../context/bundle-version';

const BundleVersionList = React.forwardRef(
  (
    {
      bundleVersions,
      onUpdate,
      onSuccess,
      className: classNameProp,
      noBorderBottom,
      edit,
      isEditMode,
    },
    ref,
  ) => {
    const className = clsx(styles.root, classNameProp, noBorderBottom && styles.noBorderBottom);

    const latestBundle = last(bundleVersions);

    const baseColumns = [
      {
        key: 'icon',
        name: 'icon',
        headerClassName: styles.iconColumnHeader,
        iconClassName: styles.headerIcon,
        ariaLabel: 'icon',
        iconName: 'CubeShape',
        isIconOnly: true,
        fieldName: 'icon',
        minWidth: 14,
        maxWidth: 14,
        columnActionsMode: 0,
        onRender: item => {
          const status = bundleStatus(item.endDate, item.locked, edit);
          return (
            <TooltipHost
              content={startCase(status)}
              calloutProps={{
                gapSpace: 0,
                target: `#${`bundleStatusIcon${kebabCase(item._id)}`}`,
              }}
            >
              <div className={clsx(styles.iconColumn, appStyles[`${status}Color`])}>
                <FontIcon
                  iconName="LocationDot"
                  ariaLabel="TextDocument"
                  id={`bundleStatusIcon${kebabCase(item._id)}`}
                />
              </div>
            </TooltipHost>
          );
        },
      },
      {
        key: 'version',
        name: 'Bundle Version',
        fieldName: 'version',
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
      },
      {
        key: 'createdBy',
        name: 'Created By',
        fieldName: 'createdBy',
        minWidth: 220,
        isResizable: true,
        onRender: ({ createdBy }) => {
          return `${createdBy}`;
        },
      },
      {
        key: 'createdDate',
        name: 'Date Created',
        fieldName: 'createdTimeStamp',
        maxWidth: 75,
        isResizable: false,
        onRender: ({ createdTimeStamp }) => {
          return formatDateString(createdTimeStamp);
        },
      },
      {
        key: 'detailsIcon',
        name: '',
        headerClassName: styles.iconColumnHeader,
        iconClassName: styles.headerIcon,
        ariaLabel: 'icon',
        isIconOnly: true,
        fieldName: 'detailsIcon',
        minWidth: 14,
        maxWidth: 14,
      },
    ];

    const VersionDetails = props => {
      // get latest version to expand only the latest version
      const isLatest = props?.item?._id === latestBundle?._id;
      return (
        <BundleVersionProvider closed={!isLatest}>
          <BundleVersionListRow
            bundleVersion={props}
            ref={ref}
            onUpdate={onUpdate}
            onSuccess={onSuccess}
            edit={edit}
            isEditMode={isEditMode}
            isLatest={isLatest}
          />
        </BundleVersionProvider>
      );
    };

    VersionDetails.propTypes = {
      item: PropTypes.shape({
        _id: PropTypes.string,
      }).isRequired,
    };

    return (
      <>
        <DetailsList
          items={bundleVersions}
          columns={baseColumns}
          onRenderRow={VersionDetails}
          collapseAllVisibility
          className={className}
        />
      </>
    );
  },
);

BundleVersionList.propTypes = {
  bundleVersions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      version: PropTypes.string,
      createdBy: PropTypes.string,
      createdTimeStamp: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      componentAllocationPercentage: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  ),
  className: PropTypes.string,
  noBorderBottom: PropTypes.bool,
  edit: PropTypes.bool,
  isEditMode: PropTypes.bool,
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
};

BundleVersionList.defaultProps = {
  bundleVersions: [],
  className: '',
  noBorderBottom: false,
  edit: false,
  isEditMode: false,
  onUpdate: () => {},
  onSuccess: () => {},
};

export default BundleVersionList;
